import type { QuestProgressEntry } from 'eolib';
import { QuestPage, QuestRequirementIcon } from 'eolib';
import { useEffect, useRef, useState } from 'preact/hooks';
import { worldStorageKey } from '@/world';
import {
  FaBoxOpen,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaCommentDots,
  FaMapMarkerAlt,
  FaSkull,
} from 'react-icons/fa';
import { UI_PANEL_BG } from '@/ui/consts';
import { useClient, useLocale } from '@/ui/context';
import { useBackdropBlur } from '@/ui/in-game';

function questIcon(icon: QuestRequirementIcon) {
  switch (icon) {
    case QuestRequirementIcon.Item:
      return <FaBoxOpen size={12} />;
    case QuestRequirementIcon.Talk:
      return <FaCommentDots size={12} />;
    case QuestRequirementIcon.Kill:
      return <FaSkull size={12} />;
    case QuestRequirementIcon.Step:
      return <FaMapMarkerAlt size={12} />;
    default:
      return <FaCheckCircle size={12} />;
  }
}

export function QuestTracker() {
  const client = useClient();
  const { locale } = useLocale();
  const blur = useBackdropBlur();

  const [trackedNames, setTrackedNames] = useState<string[]>(() => [
    ...client.questController.trackedQuestNames,
  ]);
  const [entries, setEntries] = useState<(QuestProgressEntry | null)[]>(() => {
    const progress = client.questController.progressQuests;
    return client.questController.trackedQuestNames.map(
      (name) => progress.find((q) => q.name === name) ?? null,
    );
  });
  const [collapsed, setCollapsed] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const drag = useRef<{x:number;y:number}|null>(null);
  const [position,setPosition] = useState<{x:number;y:number}|null>(()=>{
    try {const p=JSON.parse(localStorage.getItem(worldStorageKey('quest-tracker-position'))??'null');
      return p&&Number.isFinite(p.x)&&Number.isFinite(p.y)?{x:Math.max(0,Math.min(p.x,innerWidth-220)),y:Math.max(36,Math.min(p.y,innerHeight-100))}:null;
    }catch{return null;}
  });
  const move = (x:number,y:number) => {
    const rect=panel.current?.getBoundingClientRect();if(!rect)return;
    const p={x:Math.max(0,Math.min(x,innerWidth-rect.width)),y:Math.max(36,Math.min(y,innerHeight-rect.height-64))};
    setPosition(p);try{localStorage.setItem(worldStorageKey('quest-tracker-position'),JSON.stringify(p));}catch{}
  };
  useEffect(()=>{
    const resize=()=>{const r=panel.current?.getBoundingClientRect();if(r&&position)move(r.x,r.y);};
    window.addEventListener('resize',resize);return()=>window.removeEventListener('resize',resize);
  },[position]);

  useEffect(() => {
    const handleTrackedChanged = (names: string[]) => {
      setTrackedNames([...names]);
      const progress = client.questController.progressQuests;
      setEntries(names.map((n) => progress.find((q) => q.name === n) ?? null));
    };

    const handleQuestList = (
      page: QuestPage,
      progress: QuestProgressEntry[],
    ) => {
      if (page !== QuestPage.Progress) return;
      const names = client.questController.trackedQuestNames;
      setEntries(names.map((n) => progress.find((q) => q.name === n) ?? null));
    };

    client.questController.subscribeTrackedQuestChanged(handleTrackedChanged);
    client.questController.subscribeQuestListReceived(handleQuestList);

    return () => {
      client.questController.unsubscribeTrackedQuestChanged(
        handleTrackedChanged,
      );
      client.questController.unsubscribeQuestListReceived(handleQuestList);
    };
  }, [client]);

  if (trackedNames.length === 0) return null;

  return (
    <div
      ref={panel}
      data-quest-tracker
      class={`pointer-events-auto absolute top-20 right-2 lg:top-10 lg:right-16 flex flex-col gap-1.5 rounded-lg ${UI_PANEL_BG} px-2 py-1.5 ${blur}`}
      style={{ minWidth: 140, maxWidth: 220, ...(position?{left:position.x,top:position.y,right:'auto'}:{}) }}
    >
      <div class='flex items-center justify-between gap-2'>
      <button
        type='button'
        aria-label='Drag quest tracker'
        class='min-h-8 flex flex-1 cursor-grab bg-transparent p-0 text-left select-none'
        style={{touchAction:'none'}}
        onPointerDown={e=>{if(e.button!==0)return;e.stopPropagation();e.preventDefault();drag.current={x:e.clientX,y:e.clientY};e.currentTarget.setPointerCapture(e.pointerId);}}
        onPointerMove={e=>{const start=drag.current,r=panel.current?.getBoundingClientRect();if(!start||!r)return;e.stopPropagation();move(r.x+e.clientX-start.x,r.y+e.clientY-start.y);drag.current={x:e.clientX,y:e.clientY};}}
        onPointerUp={()=>{drag.current=null;}}
        onPointerCancel={()=>{drag.current=null;}}
      >
        <span class='truncate font-semibold text-primary/80 text-xs'>
          {locale.quests.trackerTitle}
        </span>
      </button>
      <button type='button' aria-label={collapsed?'Expand quest tracker':'Collapse quest tracker'}
        class='min-h-8 min-w-8 shrink-0 text-primary/60' onClick={()=>setCollapsed(c=>!c)}>
          {collapsed ? <FaChevronDown size={9} /> : <FaChevronUp size={9} />}
      </button>
      </div>

      {!collapsed && (
        <div class='flex max-h-[50vh] flex-col gap-2 overflow-y-auto overscroll-contain'>
          {trackedNames.map((name, i) => {
            const entry = entries[i] ?? null;
            const hasProgress = entry !== null && entry.target > 0;
            return (
              <div key={name} class='flex flex-col gap-0.5'>
                <div class='flex items-start gap-1.5'>
                  {entry && (
                    <span class='mt-0.5 shrink-0 text-primary'>
                      {questIcon(entry.icon)}
                    </span>
                  )}
                  <p class='font-medium text-xs leading-snug'>{name}</p>
                </div>
                {entry?.description && (
                  <p class='text-[10px] leading-snug opacity-70'>
                    {entry.description}
                  </p>
                )}
                {hasProgress && (
                  <div class='flex items-center gap-1.5'>
                    <progress
                      class='progress progress-primary h-1 flex-1'
                      value={entry!.progress}
                      max={entry!.target}
                    />
                    <span class='shrink-0 text-[10px] opacity-70'>
                      {entry!.progress}/{entry!.target}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
